import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import { useState, type ReactElement } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { useTranslation } from "react-i18next";

import { useExternalRegisterImport } from "./externalRegisterImportProvider";
import {
  canApproveReviewRow,
  canStartImport,
  getBulkApprovableRows,
  getBulkBlockedCandidateRows,
  getReviewFilterCounts,
  IMPORT_APPROVAL_BASIS_OPTIONS,
  IMPORT_APPROVAL_THRESHOLDS,
  isCandidateSelectionMissing,
  isMultipleCandidateRow,
  PAGE_SIZE,
  REVIEW_FILTERS,
  type ImportApprovalBasis,
  type ImportApprovalThreshold,
  type SummaryCounts,
} from "./externalRegisterImportModel";
import { StageUnavailable, MetricChip, WorkflowCard } from "../shared/ExternalRegisterSettingsUi";
import type {
  ImportMatchPreview,
  ImportPreviewRow,
  ImportReviewFilter,
} from "./externalRegisterImportTypes";

type ImportCandidate = NonNullable<ImportMatchPreview["candidates"]>[number];

const isImportSimulationEnabled = () => import.meta.env.VITE_EXTERNAL_REGISTER_IMPORT_SIMULATION_ENABLED === "true";

export const ReviewRowsStage = () => {
  const { t } = useTranslation();
  const {
    validateResponse,
    parseResponse,
    summaryCounts,
    activeFilter,
    setActiveFilter,
    filteredRows,
    page,
    totalPages,
    visibleRows,
    approvedRowIds,
    excludedRowIds,
    selectedPatientIds,
    approveRow,
    approveEligibleRows,
    excludeRow,
    excludeRows,
    selectCandidate,
    setPage,
    commitImport,
    simulateCommitImport,
    isCommitting,
    previewRows,
    reviewReadonly,
  } = useExternalRegisterImport();
  const importCanStart = canStartImport({
    previewRows,
    approvedRowIds,
    excludedRowIds,
    selectedPatientIds,
  });

  return (
    <WorkflowCard
      title={t("externalRegister.import.review.title")}
      sx={{ flex: 1, minHeight: 0 }}
      contentSx={{ height: "100%", minHeight: 0 }}
      bodySx={{
        height: "100%",
        minHeight: 0,
        "& > :last-child": {
          flex: 1,
          minHeight: 0,
        },
      }}
    >
      {validateResponse ? (
        <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
          <SummaryBar counts={summaryCounts} />
          {reviewReadonly && (
            <Alert severity="success" variant="outlined">
              {t("externalRegister.import.review.completionAlert")}
            </Alert>
          )}
          <ReviewFilterTabs
            activeFilter={activeFilter}
            rows={previewRows}
            onChange={setActiveFilter}
          />
          <BulkReviewActions
            rows={previewRows}
            approvedRowIds={approvedRowIds}
            excludedRowIds={excludedRowIds}
            selectedPatientIds={selectedPatientIds}
            readonly={reviewReadonly}
            onApproveEligible={approveEligibleRows}
            onExcludeRows={excludeRows}
          />
          <Typography variant="body2">
            {t("externalRegister.import.review.pagination", {
              start: filteredRows.length === 0 ? 0 : page * PAGE_SIZE + 1,
              end: Math.min((page + 1) * PAGE_SIZE, filteredRows.length),
              total: filteredRows.length,
            })}
          </Typography>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              borderRadius: 1,
            }}
          >
            <Table size="small" stickyHeader sx={{ minWidth: 980 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t("externalRegister.import.review.table.headers.row")}</TableCell>
                  <TableCell>{t("externalRegister.import.review.table.headers.status")}</TableCell>
                  <TableCell>{t("externalRegister.import.review.table.headers.sourceData")}</TableCell>
                  <TableCell>{t("externalRegister.import.review.table.headers.riskReasons")}</TableCell>
                  <TableCell>{t("externalRegister.import.review.table.headers.candidate")}</TableCell>
                  <TableCell>{t("externalRegister.import.review.table.headers.reviewAction")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row, index) => (
                  <PreviewRow
                    key={row.rowId}
                    row={row}
                    rowIndex={index}
                    approved={approvedRowIds.has(row.rowId)}
                    excluded={excludedRowIds.has(row.rowId)}
                    selectedPatientId={selectedPatientIds[row.rowId] ?? ""}
                    onApprove={() => approveRow(row.rowId)}
                    onExclude={() => excludeRow(row.rowId)}
                    onSelectPatient={(patientId) => selectCandidate(row.rowId, patientId)}
                    readonly={reviewReadonly}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ flexShrink: 0 }}>
            <Pagination
              count={totalPages}
              page={page + 1}
              onChange={(_event, value) => setPage(value - 1)}
              disabled={totalPages <= 1}
              size="small"
            />
            <Stack direction="row" spacing={1}>
              {isImportSimulationEnabled() && (
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={simulateCommitImport}
                  disabled={reviewReadonly || isCommitting || !importCanStart}
                >
                  {t("externalRegister.import.review.buttons.simulateImport")}
                </Button>
              )}
              <Button variant="contained" onClick={commitImport} disabled={reviewReadonly || isCommitting || !importCanStart}>
                {reviewReadonly
                  ? t("externalRegister.import.review.buttons.importCompleted")
                  : isCommitting
                    ? t("externalRegister.import.review.buttons.importing")
                    : t("externalRegister.import.review.buttons.startImport")}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      ) : (
        <StageUnavailable message={parseResponse ? t("externalRegister.import.review.unavailable.noValidation") : t("externalRegister.import.review.unavailable.noParse")} />
      )}
    </WorkflowCard>
  );
};

const SummaryBar = ({ counts }: { counts: SummaryCounts }) => {
  const { t } = useTranslation();

  return (
    <Paper
      variant="outlined"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 2,
        p: 1,
        backgroundColor: "background.paper",
      }}
    >
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <MetricChip label={t("externalRegister.import.review.summary.total")} value={counts.total} />
        <MetricChip label={t("externalRegister.import.review.summary.ready")} value={counts.ready} />
        <MetricChip label={t("externalRegister.import.review.summary.needsReview")} value={counts.needsReview} />
        <MetricChip label={t("externalRegister.import.review.summary.errors")} value={counts.errors} />
        <MetricChip label={t("externalRegister.import.review.summary.umrn")} value={counts.matchedOnUmrn} />
        <MetricChip label={t("externalRegister.import.review.summary.demographics")} value={counts.matchedOnDemographics} />
        <MetricChip label={t("externalRegister.import.review.summary.newPatients")} value={counts.newPatients} />
        <MetricChip label={t("externalRegister.import.review.summary.updates")} value={counts.updates} />
        <MetricChip label={t("externalRegister.import.review.summary.excluded")} value={counts.excluded} />
      </Stack>
    </Paper>
  );
};

const ReviewFilterTabs = ({
  activeFilter,
  rows,
  onChange,
}: {
  activeFilter: ImportReviewFilter;
  rows: ImportPreviewRow[];
  onChange: (filter: ImportReviewFilter) => void;
}) => {
  const { t } = useTranslation();
  const counts = getReviewFilterCounts(rows);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 0.75,
        backgroundColor: "grey.50",
        borderRadius: 1,
        overflowX: "auto",
      }}
    >
      <Tabs
        value={activeFilter}
        onChange={(_event, value) => onChange(value)}
        variant="scrollable"
        scrollButtons="auto"
        TabIndicatorProps={{ sx: { display: "none" } }}
        sx={{
          minHeight: 0,
          "& .MuiTabs-flexContainer": {
            gap: 0.75,
          },
          "& .MuiTab-root": {
            alignItems: "center",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 0.75,
            color: "text.secondary",
            minHeight: 34,
            minWidth: "auto",
            px: 1.25,
            py: 0.5,
            textTransform: "none",
            backgroundColor: "background.paper",
            "&.Mui-selected": {
              backgroundColor: "primary.main",
              borderColor: "primary.main",
              color: "primary.contrastText",
            },
            "&:hover": {
              borderColor: "primary.main",
            },
          },
        }}
      >
        {REVIEW_FILTERS.map((filter) => (
          <Tab
            key={filter.value}
            value={filter.value}
            label={<ReviewFilterTabLabel label={t(`externalRegister.import.review.filters.${filter.value}`)} count={counts[filter.value]} />}
            sx={[
              filter.value === "all"
                ? {
                  mr: 1.25,
                  position: "relative",
                  fontWeight: 800,
                  "&::after": {
                    content: "\"\"",
                    position: "absolute",
                    right: -8,
                    top: 6,
                    bottom: 6,
                    borderRight: "1px solid",
                    borderColor: "divider",
                  },
                }
                : {},
              filter.value === "errors" && counts.errors > 0
                ? {
                  borderColor: "error.main",
                  color: "error.main",
                  "&:hover": {
                    borderColor: "error.dark",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "background.paper",
                    borderColor: "error.main",
                    color: "error.main",
                  },
                  "& .MuiChip-root": {
                    backgroundColor: "error.main",
                    color: "error.contrastText",
                  },
                  "&.Mui-selected .MuiChip-root": {
                    backgroundColor: "error.main",
                    color: "error.contrastText",
                  },
                }
                : {},
            ]}
          />
        ))}
      </Tabs>
    </Paper>
  );
};

const ReviewFilterTabLabel = ({ label, count }: { label: string; count: number }) => {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1 }}>
        {label}
      </Typography>
      <Chip
        size="small"
        label={count}
        sx={{
          height: 20,
          minWidth: 28,
          borderRadius: 0.75,
          fontSize: "0.7rem",
          fontWeight: 800,
          backgroundColor: "action.selected",
          color: "inherit",
          "& .MuiChip-label": {
            px: 0.75,
          },
        }}
      />
    </Stack>
  );
};

const BulkReviewActions = ({
  rows,
  approvedRowIds,
  excludedRowIds,
  selectedPatientIds,
  readonly,
  onApproveEligible,
  onExcludeRows,
}: {
  rows: ImportPreviewRow[];
  approvedRowIds: Set<string>;
  excludedRowIds: Set<string>;
  selectedPatientIds: Record<string, string>;
  readonly: boolean;
  onApproveEligible: (threshold: ImportApprovalThreshold, basis?: ImportApprovalBasis) => void;
  onExcludeRows: (rowIds: string[]) => void;
}) => {
  const { t } = useTranslation();
  const [threshold, setThreshold] = useState<ImportApprovalThreshold>("MEDIUM");
  const [basis, setBasis] = useState<ImportApprovalBasis>("allMatched");
  const eligibleRows = getBulkApprovableRows({
    rows,
    threshold,
    basis,
    approvedRowIds,
    excludedRowIds,
    selectedPatientIds,
  });
  const blockedCandidateRows = getBulkBlockedCandidateRows({
    rows,
    threshold,
    basis,
    approvedRowIds,
    excludedRowIds,
    selectedPatientIds,
  });
  const openReviewRows = rows.filter((row) =>
    row.reviewPriority === "NEEDS_REVIEW"
    && !approvedRowIds.has(row.rowId)
    && !excludedRowIds.has(row.rowId),
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1,
        borderRadius: 1,
        backgroundColor: "background.paper",
      }}
    >
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", lg: "center" }}
        justifyContent="space-between"
      >
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {t("externalRegister.import.review.bulk.title")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("externalRegister.import.review.bulk.caption")}
          </Typography>
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={0.75} alignItems={{ xs: "stretch", sm: "center" }}>
          <FormControl size="small" sx={{ minWidth: 174 }}>
            <InputLabel id="bulk-review-threshold-label">{t("externalRegister.import.review.bulk.confidence")}</InputLabel>
            <Select
              labelId="bulk-review-threshold-label"
              label={t("externalRegister.import.review.bulk.confidence")}
              value={threshold}
              onChange={(event) => setThreshold(event.target.value as ImportApprovalThreshold)}
              disabled={readonly}
              sx={{
                minHeight: 34,
                "& .MuiSelect-select": {
                  py: 0.75,
                },
              }}
            >
              {IMPORT_APPROVAL_THRESHOLDS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(`externalRegister.import.review.bulk.thresholds.${option.value}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 188 }}>
            <InputLabel id="bulk-review-basis-label">{t("externalRegister.import.review.bulk.basis.label")}</InputLabel>
            <Select
              labelId="bulk-review-basis-label"
              label={t("externalRegister.import.review.bulk.basis.label")}
              value={basis}
              onChange={(event) => setBasis(event.target.value as ImportApprovalBasis)}
              disabled={readonly}
              sx={{
                minHeight: 34,
                "& .MuiSelect-select": {
                  py: 0.75,
                },
              }}
            >
              {IMPORT_APPROVAL_BASIS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(`externalRegister.import.review.bulk.basis.${option.value}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onApproveEligible(threshold, basis)}
            disabled={readonly || eligibleRows.length === 0}
            sx={{ minHeight: 34, whiteSpace: "nowrap" }}
          >
            {t("externalRegister.import.review.bulk.buttons.approveEligible", { count: eligibleRows.length })}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            onClick={() => onExcludeRows(openReviewRows.map((row) => row.rowId))}
            disabled={readonly || openReviewRows.length === 0}
            sx={{ minHeight: 34, whiteSpace: "nowrap" }}
          >
            {t("externalRegister.import.review.bulk.buttons.excludeOpen", { count: openReviewRows.length })}
          </Button>
        </Stack>
      </Stack>
      {blockedCandidateRows.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
          {t("externalRegister.import.review.bulk.blockedCaption", {
            count: blockedCandidateRows.length,
            row: blockedCandidateRows.length === 1
              ? t("externalRegister.import.review.bulk.rowSingular")
              : t("externalRegister.import.review.bulk.rowPlural"),
            needs: blockedCandidateRows.length === 1
              ? t("externalRegister.import.review.bulk.needsSingular")
              : t("externalRegister.import.review.bulk.needsPlural"),
          })}
        </Typography>
      )}
    </Paper>
  );
};

const PreviewRow = ({
  row,
  rowIndex,
  approved,
  excluded,
  selectedPatientId,
  onApprove,
  onExclude,
  onSelectPatient,
  readonly,
}: {
  row: ImportPreviewRow;
  rowIndex: number;
  approved: boolean;
  excluded: boolean;
  selectedPatientId: string;
  onApprove: () => void;
  onExclude: () => void;
  onSelectPatient: (patientId: string) => void;
  readonly: boolean;
}) => {
  const { t } = useTranslation();
  const statusChip = getRowStatusChip(row, t);
  const requiresCandidate = isMultipleCandidateRow(row);
  const candidateRequiredButMissing = isCandidateSelectionMissing(row, selectedPatientId);

  return (
    <TableRow sx={{ backgroundColor: rowIndex % 2 === 0 ? "background.paper" : "grey.50" }}>
      <TableCell>{row.rowNumber}</TableCell>
      <TableCell>
        <Stack spacing={0.5}>
          <Chip
            size="small"
            color={statusChip.color}
            variant={statusChip.variant}
            icon={statusChip.icon}
            label={statusChip.label}
          />
          {excluded && <Chip size="small" label={t("externalRegister.import.review.row.excluded")} />}
          {approved && <Chip size="small" color="success" label={t("externalRegister.import.review.row.approved")} />}
        </Stack>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {row.mappedRecord?.givenName} {row.mappedRecord?.surname}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {row.mappedRecord?.umrn || t("externalRegister.import.review.row.noUmrn")} - {row.mappedRecord?.type}
        </Typography>
      </TableCell>
      <TableCell>
        <Stack spacing={0.5}>
          {[...row.reviewReasons, ...row.errors, ...(row.commitError ? [row.commitError] : [])].map((reason) => (
            <Typography key={reason} variant="caption">
              {reason}
            </Typography>
          ))}
        </Stack>
      </TableCell>
      <TableCell>
        {requiresCandidate ? (
          <CandidateChoices
            row={row}
            readonly={readonly}
            selectedPatientId={selectedPatientId}
            onSelectPatient={onSelectPatient}
          />
        ) : (
          <SingleCandidateSummary row={row} />
        )}
      </TableCell>
      <TableCell>
        {row.reviewPriority === "NEEDS_REVIEW" ? (
          <Stack spacing={1}>
            {candidateRequiredButMissing && (
              <Typography variant="caption" color="text.secondary">
                {t("externalRegister.import.review.row.candidateRequired")}
              </Typography>
            )}
            <Stack spacing={0.75} alignItems="stretch">
              <Button
                size="small"
                variant={approved ? "contained" : "outlined"}
                onClick={onApprove}
                disabled={readonly || !canApproveReviewRow(row, selectedPatientId)}
                sx={{ minWidth: 96 }}
              >
                {t("externalRegister.import.review.row.buttons.approve")}
              </Button>
              <Button
                size="small"
                variant={excluded ? "contained" : "outlined"}
                color="warning"
                onClick={onExclude}
                disabled={readonly}
                sx={{ minWidth: 96 }}
              >
                {t("externalRegister.import.review.row.buttons.exclude")}
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {row.reviewPriority === "READY" ? t("externalRegister.import.review.row.secondaryStatus.autoReady") : t("externalRegister.import.review.row.secondaryStatus.cannotCommit")}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
};

const SingleCandidateSummary = ({ row }: { row: ImportPreviewRow }) => {
  const { t } = useTranslation();
  const candidate = getSingleCandidate(row);
  if (!candidate) {
    return (
      <Typography variant="body2" color="text.secondary">
        {row.matchPreview?.matchedOn === "CREATED_NEW" ? t("externalRegister.import.review.row.candidate.createsNew") : t("externalRegister.import.review.row.candidate.noCandidate")}
      </Typography>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 0.75, borderRadius: 1, backgroundColor: "background.paper" }}>
      <CandidatePatientSummary candidate={candidate} />
    </Paper>
  );
};

const getSingleCandidate = (row: ImportPreviewRow): ImportCandidate | null => {
  const explicitCandidate = row.matchPreview?.candidates?.[0];
  if (explicitCandidate) return explicitCandidate;
  const patientId = row.matchPreview?.patientId;
  if (!patientId) return null;
  return {
    patientId,
    firstName: typeof row.mappedRecord?.givenName === "string" ? row.mappedRecord.givenName : undefined,
    lastName: typeof row.mappedRecord?.surname === "string" ? row.mappedRecord.surname : undefined,
    dateOfBirth: typeof row.mappedRecord?.dob === "string" ? row.mappedRecord.dob : undefined,
    sex: typeof row.mappedRecord?.sex === "string" ? row.mappedRecord.sex : undefined,
    umrn: typeof row.mappedRecord?.umrn === "string" ? row.mappedRecord.umrn : undefined,
  };
};

const getRowStatusChip = (row: ImportPreviewRow, t: (key: string, options?: Record<string, unknown>) => string): {
  color: "default" | "error" | "success";
  icon?: ReactElement;
  label: string;
  variant?: "filled" | "outlined";
} => {
  if (row.reviewPriority === "ERROR") {
    return {
      color: "error",
      icon: <ErrorIcon />,
      label: t("externalRegister.import.review.row.status.error"),
    };
  }

  if (row.reviewPriority === "READY") {
    return {
      color: "success",
      icon: <CheckCircleIcon />,
      label: t("externalRegister.import.review.row.status.readyPrefix", { confidence: row.matchConfidence }),
    };
  }

  return {
    color: "default",
    label: row.matchConfidence,
    variant: "outlined",
  };
};

const CandidateChoices = ({
  row,
  readonly,
  selectedPatientId,
  onSelectPatient,
}: {
  row: ImportPreviewRow;
  readonly: boolean;
  selectedPatientId: string;
  onSelectPatient: (patientId: string) => void;
}) => {
  const { t } = useTranslation();

  return (
    <Stack spacing={1}>
      <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
        {t("externalRegister.import.review.row.selectCandidateAlert")}
      </Alert>
      <RadioGroup value={selectedPatientId} onChange={(event) => onSelectPatient(event.target.value)} sx={{ gap: 1 }}>
        {row.matchPreview?.candidates?.map((candidate) => (
          <FormControlLabel
            key={candidate.patientId}
            value={candidate.patientId}
            control={<Radio size="small" disabled={readonly} sx={{ pt: 0.75 }} />}
            label={<CandidatePatientSummary candidate={candidate} />}
            sx={{
              alignItems: "flex-start",
              border: 1,
              borderColor: selectedPatientId === candidate.patientId ? "primary.main" : "divider",
              borderRadius: 1,
              m: 0,
              px: 0.75,
              py: 0.75,
              backgroundColor: selectedPatientId === candidate.patientId ? "primary.50" : "background.paper",
              "& .MuiFormControlLabel-label": {
                minWidth: 0,
                width: "100%",
              },
            }}
          />
        ))}
      </RadioGroup>
    </Stack>
  );
};

const CandidatePatientSummary = ({ candidate }: { candidate: ImportCandidate }) => {
  const { t } = useTranslation();
  const displayName = [candidate.firstName, candidate.lastName].filter(Boolean).join(" ") || t("externalRegister.import.review.candidateCard.unnamedPatient");
  const details = getCandidateDetails(candidate, t);

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" noWrap sx={{ fontWeight: 900, minWidth: 0 }}>
        {displayName}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
        {t("externalRegister.import.review.candidateCard.patientId", { patientId: candidate.patientId })}
      </Typography>
      {details.length ? (
        <Box sx={{
          display: "grid",
          gap: 0.75,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(3, minmax(0, 1fr))",
          },
          mt: 0.75,
        }}>
          {details.map((detail) => (
            <Box
              key={`${candidate.patientId}-${detail.label}`}
              sx={{
                minWidth: 0,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                px: 0.75,
                py: 0.5,
                backgroundColor: "grey.50",
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 800, lineHeight: 1.1 }}>
                {detail.label}
              </Typography>
              <Typography variant="body2" noWrap title={detail.value} sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                {detail.value}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

const getCandidateDetails = (candidate: ImportCandidate, t: (key: string, options?: Record<string, unknown>) => string): Array<{ label: string; value: string }> => [
  { label: t("externalRegister.import.review.candidateCard.fields.umrn"), value: candidate.umrn ?? "" },
  { label: t("externalRegister.import.review.candidateCard.fields.dob"), value: candidate.dateOfBirth ?? "" },
  { label: t("externalRegister.import.review.candidateCard.fields.sex"), value: candidate.sex ?? "" },
  { label: t("externalRegister.import.review.candidateCard.fields.medicare"), value: [candidate.medicareNo, candidate.medicareNoPos ? t("externalRegister.import.review.candidateCard.fields.medicareIrn", { irn: candidate.medicareNoPos }) : undefined].filter(Boolean).join(" · ") },
  { label: t("externalRegister.import.review.candidateCard.fields.phone"), value: candidate.phone ?? "" },
  { label: t("externalRegister.import.review.candidateCard.fields.email"), value: candidate.email ?? "" },
].filter((detail) => detail.value);
